import { type AnchorHTMLAttributes, type MouseEvent, useSyncExternalStore } from 'react';

import type { Area } from '../../shared/model';

export const pagePaths = {
  Today: '/today',
  Tasks: '/tasks',
  Calendar: '/calendar',
  Goals: '/goals',
  Reviews: '/reviews',
  Learning: '/learning',
  People: '/people',
  Trash: '/trash',
  Lab: '/settings',
  Vault: '/vault',
  Inbox: '/library/inbox',
  Projects: '/library/projects',
  Areas: '/library/areas',
  Resources: '/library/resources',
  Archive: '/library/archive',
  Notes: '/notes',
  More: '/more',
} as const;

export type Page = keyof typeof pagePaths;
export type Route = { page: Page | 'Task' | 'Note' | 'Not found'; id?: string };

export const taskPath = (id: string) => `/tasks/${encodeURIComponent(id)}`;
export const notePath = (id: string) => `/notes/${encodeURIComponent(id)}`;

export function parseRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/') {
    return { page: 'Today' };
  }

  const page = (Object.keys(pagePaths) as Page[]).find(key => pagePaths[key] === path);

  if (page) {
    return { page };
  }

  const match = /^\/(tasks|notes)\/([^/]+)$/.exec(path);

  if (match) {
    try {
      const id = decodeURIComponent(match[2]);

      if (/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(id)) {
        return { page: match[1] === 'tasks' ? 'Task' : 'Note', id };
      }
    } catch {
      /* Invalid URL encoding is an unknown route. */
    }
  }

  return { page: 'Not found' };
}

const subscribe = (notify: () => void) => {
  window.addEventListener('popstate', notify);

  return () => window.removeEventListener('popstate', notify);
};

export function goTo(path: string) {
  const target = new URL(path, location.origin);

  if (target.origin !== location.origin) {
    throw new Error('Internal navigation requires a same-origin URL.');
  }

  if (target.href === location.href) {
    return;
  }

  history.pushState(null, '', target.pathname + target.search + target.hash);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute() {
  const path = useSyncExternalStore(
    subscribe,
    () => location.pathname,
    () => '/',
  );

  return parseRoute(path);
}

export function RouteLink({
  href,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  function follow(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (props.target && props.target !== '_self') ||
      props.download !== undefined
    ) {
      return;
    }

    if (new URL(href, location.origin).origin !== location.origin) {
      return;
    }

    event.preventDefault();
    goTo(href);
  }

  return <a {...props} href={href} onClick={follow} />;
}

export const isArea = (page: string): page is Area =>
  ['Inbox', 'Projects', 'Areas', 'Resources', 'Archive'].includes(page);
